import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Sparkles } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { SectionHead } from "@/components/landing/Section";
import { SUPPORT_PACKAGES } from "@/config/supporter";

const PackageCard = ({ pkg }) => {
  const { t, fmtNum } = useLang();
  const s = t.pages.supporter;
  const p = s.packages[pkg.key];
  const dark = pkg.popular;
  return (
    <div
      data-testid={`supporter-package-${pkg.key}`}
      className={`relative flex flex-col rounded-[1.75rem] p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 ${
        dark ? "bg-ink text-parchment shadow-journal-lg lg:-mt-4 lg:mb-4" : "journal-card hover:shadow-journal-lg"
      }`}
    >
      {dark && (
        <span className="absolute -top-3 left-7 inline-flex items-center gap-1.5 rounded-full bg-amber text-parchment px-3 py-1 text-xs font-extrabold uppercase tracking-wider">
          <Sparkles className="w-3.5 h-3.5" />
          {s.popular}
        </span>
      )}
      <div className={`text-xs font-extrabold uppercase tracking-[0.16em] ${dark ? "text-amber" : "text-amber-dark"}`}>
        {pkg.trolls === 1 ? s.troll : s.trolls.replace("{n}", pkg.trolls)}
      </div>
      <h3 className="mt-2 font-display text-2xl font-semibold">{p.name}</h3>
      <p className={`mt-1 text-sm ${dark ? "text-parchment/70" : "text-ink-muted"}`}>{p.for}</p>
      <div className="mt-6">
        <div className={`text-sm line-through ${dark ? "text-parchment/50" : "text-ink-muted/70"}`} data-testid={`supporter-package-${pkg.key}-regular`}>
          {s.regular}: {fmtNum(pkg.regular)} kr
        </div>
        <div className="font-display text-4xl font-semibold tabular-nums" data-testid={`supporter-package-${pkg.key}-price`}>
          {fmtNum(pkg.launch)} kr
        </div>
        <div className={`mt-1 text-sm font-bold ${dark ? "text-parchment/70" : "text-ink-muted"}`}>{s.perYear}</div>
        <div className={`mt-1 text-sm font-extrabold ${dark ? "text-amber" : "text-moss"}`}>
          {s.perTroll.replace("{price}", fmtNum(Math.round(pkg.launch / pkg.trolls)))}
        </div>
      </div>
      <ul className="mt-6 space-y-2.5 flex-1">
        {p.perks.map((perk) => (
          <li key={perk} className="flex gap-2.5 text-sm font-semibold">
            <Check className={`w-4 h-4 mt-0.5 shrink-0 ${dark ? "text-amber" : "text-moss"}`} />
            {perk}
          </li>
        ))}
      </ul>
      <Link
        to={{ search: `?package=${pkg.key}`, hash: "#signup" }}
        data-testid={`supporter-package-${pkg.key}-cta`}
        className={`mt-7 inline-flex justify-center items-center gap-2 rounded-full px-5 py-3 font-extrabold transition-colors ${
          dark ? "bg-amber hover:bg-amber-dark text-parchment" : "bg-moss hover:bg-moss-dark text-parchment"
        }`}
      >
        {s.choose.replace("{name}", p.name)}
        <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
};

export default function Packages() {
  const { t } = useLang();
  const s = t.pages.supporter;
  return (
    <section id="packages" data-testid="supporter-packages" className="scroll-mt-28">
      <SectionHead kicker={s.pkgKicker} title={s.pkgTitle} body={s.pkgLead} />
      <p data-testid="supporter-launch-note" className="mt-6 inline-flex rounded-full bg-rowan/10 border border-rowan/30 px-4 py-2 text-sm font-extrabold text-rowan">
        {s.launchNote}
      </p>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:items-stretch">
        {SUPPORT_PACKAGES.map((pkg) => <PackageCard key={pkg.key} pkg={pkg} />)}
      </div>
      <div className="mt-8 rounded-[1.75rem] border-2 border-dashed border-line p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-5">
        <p className="font-display text-lg md:text-xl text-ink max-w-3xl">{s.custom}</p>
        <Link to="/contact?package=custom" data-testid="supporter-custom-cta" className="shrink-0 inline-flex items-center gap-2 rounded-full border-2 border-ink px-6 py-3 font-extrabold text-ink hover:bg-ink hover:text-parchment transition-colors">
          {s.customCta}
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}
